import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQuery } from 'react-query';
import { JobHttpService } from 'cms/features/feature_job/service';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Alert,
    Box,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const EMPLOYMENT_TYPES = [
    { value: 'Full-time', label: 'Full-time' },
    { value: 'Part-time', label: 'Part-time' },
    { value: 'Temporary', label: 'Temporary' },
    { value: 'Contract', label: 'Contract' },
    { value: 'Internship', label: 'Internship' }
];

function ContractForm({ open, onClose, contractId, companyId, onSaved, onSaveAndAddAnother }) {
    const { register, handleSubmit, formState: { errors }, reset, control } = useForm();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [loadingContract, setLoadingContract] = useState(false);

    // Load employees for the company
    const { data: employeesData } = useQuery(
        ['employees', companyId],
        () => {
            if (!companyId) return { results: [], count: 0 };
            return JobHttpService.loadEmployees({ company: companyId });
        },
        {
            enabled: !!companyId,
            keepPreviousData: true
        }
    );

    const employees = employeesData?.results || employeesData || [];

    useEffect(() => {
        if (open) {
            if (contractId) {
                loadContract();
            } else {
                reset({
                    employee: '',
                    contract_start_date: null,
                    contract_end_date: null,
                    contract_details: '',
                    employment_type: '',
                    employment_notice_period: '',
                    employment_place: '',
                    employment_hours_per_week: '',
                    employee_position_title: '',
                    salary: '',
                    salary_payment_date: null,
                    holiday_entitlement: '',
                    collective_agreement: '',
                    insurance_details: ''
                });
                setError('');
            }
        }
    }, [open, contractId, reset]);

    const loadContract = async () => {
        if (!contractId) return;
        
        setLoadingContract(true);
        setError('');
        try {
            const contract = await JobHttpService.loadContract(contractId);
            reset({
                employee: contract.employee?.id || '',
                contract_start_date: contract.contract_start_date ? dayjs(contract.contract_start_date) : null,
                contract_end_date: contract.contract_end_date ? dayjs(contract.contract_end_date) : null,
                contract_details: contract.contract_details || '',
                employment_type: contract.employment_type || '',
                employment_notice_period: contract.employment_notice_period || '',
                employment_place: contract.employment_place || '',
                employment_hours_per_week: contract.employment_hours_per_week || '',
                employee_position_title: contract.employee_position_title || '',
                salary: contract.salary || '',
                salary_payment_date: contract.salary_payment_date ? dayjs(contract.salary_payment_date) : null,
                holiday_entitlement: contract.holiday_entitlement || '',
                collective_agreement: contract.collective_agreement || '',
                insurance_details: contract.insurance_details || ''
            });
        } catch (err) {
            console.error('Error loading contract:', err);
            setError(err?.message || 'Failed to load contract');
        } finally {
            setLoadingContract(false);
        }
    };

    const onSubmit = async (data, saveAndAddAnother = false) => {
        setLoading(true);
        setError('');

        try {
            const submitData = {
                employee: data.employee,
                company: companyId,
                contract_start_date: data.contract_start_date ? dayjs(data.contract_start_date).format('YYYY-MM-DD') : null,
                contract_end_date: data.contract_end_date ? dayjs(data.contract_end_date).format('YYYY-MM-DD') : null,
                contract_details: data.contract_details || null,
                employment_type: data.employment_type || null,
                employment_notice_period: data.employment_notice_period || null,
                employment_place: data.employment_place || null,
                employment_hours_per_week: data.employment_hours_per_week ? parseInt(data.employment_hours_per_week) : null,
                employee_position_title: data.employee_position_title || null,
                salary: data.salary ? parseInt(data.salary) : null,
                salary_payment_date: data.salary_payment_date ? dayjs(data.salary_payment_date).format('YYYY-MM-DD') : null,
                holiday_entitlement: data.holiday_entitlement || null,
                collective_agreement: data.collective_agreement || null,
                insurance_details: data.insurance_details || null
            };

            let response;
            if (contractId) {
                response = await JobHttpService.updateContract(contractId, submitData);
            } else {
                response = await JobHttpService.createContract(submitData);
            }

            if (response && !response.detail && !response.error) {
                if (onSaved) {
                    onSaved();
                }
                
                if (saveAndAddAnother) {
                    reset({
                        employee: '',
                        contract_start_date: null,
                        contract_end_date: null,
                        contract_details: '',
                        employment_type: '',
                        employment_notice_period: '',
                        employment_place: '',
                        employment_hours_per_week: '',
                        employee_position_title: '',
                        salary: '',
                        salary_payment_date: null,
                        holiday_entitlement: '',
                        collective_agreement: '',
                        insurance_details: ''
                    });
                    setError('');
                    if (onSaveAndAddAnother) {
                        onSaveAndAddAnother();
                    }
                } else {
                    onClose();
                }
            } else {
                setError(response?.detail || response?.error || 'Failed to save contract');
            }
        } catch (err) {
            console.error('Error saving contract:', err);
            setError(err?.message || 'Failed to save contract. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <form onSubmit={handleSubmit((data) => onSubmit(data, false))}>
                <DialogTitle>
                    {contractId ? 'Edit Contract' : 'Create New Contract'}
                </DialogTitle>
                <DialogContent>
                    {loadingContract && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {!loadingContract && (
                        <>
                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Employee *</InputLabel>
                                        <Select
                                            label="Employee *"
                                            {...register("employee", {
                                                required: "Employee is required"
                                            })}
                                            error={Boolean(errors.employee)}
                                        >
                                            {employees.map((employee) => (
                                                <MenuItem key={employee.id} value={employee.id}>
                                                    {employee.first_name} {employee.last_name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {errors.employee && (
                                        <Alert severity="error" sx={{ mt: 0.5 }}>
                                            {errors.employee.message}
                                        </Alert>
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <Controller
                                            name="contract_start_date"
                                            control={control}
                                            render={({ field }) => (
                                                <DatePicker
                                                    label="Contract Start Date"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    slotProps={{ textField: { fullWidth: true } }}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <Controller
                                            name="contract_end_date"
                                            control={control}
                                            render={({ field }) => (
                                                <DatePicker
                                                    label="Contract End Date"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    slotProps={{ textField: { fullWidth: true } }}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Employment Type</InputLabel>
                                        <Select
                                            label="Employment Type"
                                            {...register("employment_type")}
                                        >
                                            <MenuItem value="">None</MenuItem>
                                            {EMPLOYMENT_TYPES.map((type) => (
                                                <MenuItem key={type.value} value={type.value}>
                                                    {type.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Position Title"
                                        {...register("employee_position_title")}
                                        error={Boolean(errors.employee_position_title)}
                                        helperText={errors.employee_position_title?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Employment Place"
                                        {...register("employment_place")}
                                        error={Boolean(errors.employment_place)}
                                        helperText={errors.employment_place?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Hours per Week"
                                        type="number"
                                        {...register("employment_hours_per_week")}
                                        error={Boolean(errors.employment_hours_per_week)}
                                        helperText={errors.employment_hours_per_week?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Salary"
                                        type="number"
                                        {...register("salary")}
                                        error={Boolean(errors.salary)}
                                        helperText={errors.salary?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <Controller
                                            name="salary_payment_date"
                                            control={control}
                                            render={({ field }) => (
                                                <DatePicker
                                                    label="Salary Payment Date"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    slotProps={{ textField: { fullWidth: true } }}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Notice Period"
                                        {...register("employment_notice_period")}
                                        error={Boolean(errors.employment_notice_period)}
                                        helperText={errors.employment_notice_period?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Holiday Entitlement"
                                        {...register("holiday_entitlement")}
                                        error={Boolean(errors.holiday_entitlement)}
                                        helperText={errors.holiday_entitlement?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Collective Agreement"
                                        {...register("collective_agreement")}
                                        error={Boolean(errors.collective_agreement)}
                                        helperText={errors.collective_agreement?.message}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Insurance Details"
                                        multiline
                                        rows={3}
                                        {...register("insurance_details")}
                                        error={Boolean(errors.insurance_details)}
                                        helperText={errors.insurance_details?.message}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Contract Details"
                                        multiline
                                        rows={5}
                                        {...register("contract_details")}
                                        error={Boolean(errors.contract_details)}
                                        helperText={errors.contract_details?.message}
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={loading || loadingContract}
                    >
                        {loading ? <CircularProgress size={20} /> : (contractId ? 'Update' : 'Save')}
                    </Button>
                    {!contractId && (
                        <Button
                            variant="outlined"
                            fullWidth
                            disabled={loading || loadingContract}
                            onClick={handleSubmit((data) => onSubmit(data, true))}
                        >
                            Save and add another
                        </Button>
                    )}
                    <Button
                        onClick={onClose}
                        disabled={loading}
                        fullWidth
                        color="inherit"
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export default ContractForm;
